import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {NotificationService} from '../../../components/notification/notification.service';
import {Subscription, timer} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {GridDataResult, PageChangeEvent, RowClassArgs} from '@progress/kendo-angular-grid';
import {
    PublishJourneyKey,
    RarsPublishService
} from '../../../../core/services/rars-publish/rars-publish.service';

/**
 * Review-then-send dialog for publishing re-opt and business-rule results to RARS.
 *
 * The grid shows the payload as actually staged, not a re-derivation of it, so
 * what the user approves is what gets sent. One row per journey, buckets beneath;
 * each journey is ticked by default and can be unticked to hold it back from the
 * send. Exclusions (not inclusions) are tracked, so default-ticked works across
 * server pages without loading every key.
 */
@Component({
    selector: 'app-rars-publish-dialog',
    templateUrl: './rars-publish-dialog.component.html',
    styleUrls: ['./rars-publish-dialog.component.scss']
})
export class RarsPublishDialogComponent implements OnInit, OnDestroy {

    @Input() public requestId: string;
    /** An already-staged payload to open directly; when absent, a fresh stage
     *  is requested for requestId (legacy path -- normally the toolbar only
     *  offers live payloads, so this is set). */
    @Input() public publishId: string;
    @Output() public closed = new EventEmitter<boolean>();

    public job: any = null;
    /** The current page of journeys, each carrying its `buckets`. */
    public items: any[] = [];
    /** Journeys in the payload, not buckets. */
    public total = 0;
    public skip = 0;
    public pageSize = 100;
    public loading = true;
    public sending = false;
    public errorMessage = '';

    // a staged payload goes stale as bookings arrive, so warn past this age
    private stalenessMinutes = 30;
    private pollSubscription: Subscription;
    /** Journeys the user unticked, by journey key. */
    private excluded = new Map<string, PublishJourneyKey>();
    /** Journeys the user has toggled this session, so a reload does not undo them. */
    private touched = new Set<string>();

    constructor(private rarsPublishService: RarsPublishService,
                private notificationService: NotificationService) {
    }

    public ngOnInit(): void {
        // an offered payload already lives in the database: open it directly
        // rather than staging anything new
        if (this.publishId) {
            this.startPolling(this.publishId);
        } else {
            this.stage();
        }
    }

    public ngOnDestroy(): void {
        // guard: the existing notification menu throws here when it never polled
        if (this.pollSubscription) {
            this.pollSubscription.unsubscribe();
        }
    }

    /** Ask the backend to build the payload, then poll until it is ready. */
    public stage(): void {
        this.loading = true;
        this.errorMessage = '';
        this.rarsPublishService.stage(this.requestId).subscribe(result => {
            this.startPolling(result.publishId);
        }, error => {
            this.loading = false;
            this.errorMessage = this.readError(error, 'Could not start the publish.');
        });
    }

    /** The grid pages server-side, so `items` is one page and `total` the full count. */
    public get gridView(): GridDataResult {
        return {data: this.items, total: this.total};
    }

    public get isPendingReview(): boolean {
        return this.job && this.job.status === 'Pending Review';
    }

    public get isBlocked(): boolean {
        return this.job && this.job.status === 'Blocked';
    }

    public get canSend(): boolean {
        return this.isPendingReview && !this.sending && this.selectedCount > 0;
    }

    /** Journeys that will go with the send: everything not unticked. */
    public get selectedCount(): number {
        return Math.max(this.total - this.excluded.size, 0);
    }

    public isIncluded(item: any): boolean {
        return !this.excluded.has(this.journeyKey(item));
    }

    public toggleJourney(item: any): void {
        const key = this.journeyKey(item);
        this.touched.add(key);
        if (this.excluded.has(key)) {
            this.excluded.delete(key);
        } else {
            this.excluded.set(key, this.toJourneyKey(item));
        }
    }

    /** Grey out an unticked row so the state reads at a glance. */
    public rowClass = (context: RowClassArgs): {[key: string]: boolean} => ({
        'publish-row-excluded': !this.isIncluded(context.dataItem)
    });

    /** True once the staged numbers are old enough that bookings may have moved. */
    public get isStale(): boolean {
        if (!this.job || !this.job.stagedDate) {
            return false;
        }
        const staged = new Date(this.job.stagedDate).getTime();
        return (Date.now() - staged) / 60000 > this.stalenessMinutes;
    }

    public onPageChange(event: PageChangeEvent): void {
        this.skip = event.skip;
        this.loadItems();
    }

    public onSend(): void {
        this.sending = true;
        this.errorMessage = '';
        const excludedJourneys = Array.from(this.excluded.values());
        this.rarsPublishService.confirm(this.job.publishId, excludedJourneys).subscribe(() => {
            // the job row just moved to Sending (or Sent, on a demo tenant): show it in
            // the bell now rather than on the next minute's poll
            this.notificationService.refresh();
            this.closed.emit(true);
        }, error => {
            this.sending = false;
            this.errorMessage = this.readError(error, 'Could not send to RARS.');
        });
    }

    public onClose(): void {
        // Closing keeps a Pending Review payload: it took a pipeline run to
        // build, blocks nothing, and reopening it is instant. Only a job still
        // Requested is cancelled -- a staging that never started (e.g. the
        // pipeline could not schedule) counts as in-flight and would otherwise
        // block every later publish with a permanent "Sending..." toolbar.
        if (this.job && this.job.status === 'Requested') {
            this.rarsPublishService.cancel(this.job.publishId).subscribe();
        }
        this.closed.emit(false);
    }

    /** The user chose not to send: the payload is ignored by the system from
     *  here on but kept for RA technicians to review. */
    public onDiscard(): void {
        this.errorMessage = '';
        this.rarsPublishService.discard(this.job.publishId).subscribe(() => {
            this.closed.emit(false);
        }, error => {
            this.errorMessage = this.readError(error, 'Could not discard the payload.');
        });
    }

    public get canDiscard(): boolean {
        return this.isPendingReview && !this.sending;
    }

    private startPolling(publishId: string): void {
        this.pollSubscription = timer(0, 4000).pipe(
            switchMap(() => this.rarsPublishService.getJob(publishId)),
            tap(job => {
                this.job = job;
                if (job.status === 'Pending Review') {
                    this.loading = false;
                    this.loadItems();
                    this.pollSubscription.unsubscribe();
                } else if (this.isTerminal(job.status)) {
                    this.loading = false;
                    this.errorMessage = job.note || `Publish ${job.status}.`;
                    this.pollSubscription.unsubscribe();
                }
            })
        ).subscribe(() => {
        }, error => {
            this.loading = false;
            this.errorMessage = this.readError(error, 'Lost track of the publish.');
        });
    }

    private isTerminal(status: string): boolean {
        return ['Blocked', 'Failed', 'Cancelled', 'Expired', 'Sent', 'Partial'].indexOf(status) >= 0;
    }

    private loadItems(): void {
        this.rarsPublishService.getJourneys(this.job.publishId, this.skip, this.pageSize)
            .subscribe(result => {
                this.items = result.items || [];
                this.total = result.total;
                this.seedExclusions();
            }, error => {
                this.errorMessage = this.readError(error, 'Could not load the payload.');
            });
    }

    /**
     * A confirm that was reverted (e.g. the send trigger failed to write) leaves
     * the unticked journeys Excluded in the database; show them unticked again
     * unless the user has already toggled them here.
     */
    private seedExclusions(): void {
        this.items
            .filter(item => item.sendStatus === 'Excluded')
            .forEach(item => {
                const key = this.journeyKey(item);
                if (!this.touched.has(key)) {
                    this.excluded.set(key, this.toJourneyKey(item));
                }
            });
    }

    private journeyKey(item: any): string {
        return [item.departureDate, item.rsid, item.journeyOrigin,
            item.journeyDestination, item.passengerClass].join('|');
    }

    private toJourneyKey(item: any): PublishJourneyKey {
        return {
            rsid: item.rsid,
            departureDate: item.departureDate,
            journeyOrigin: item.journeyOrigin,
            journeyDestination: item.journeyDestination,
            passengerClass: item.passengerClass
        };
    }

    private readError(error: any, fallback: string): string {
        if (error && error.error && error.error.error) {
            return error.error.error;
        }
        return fallback;
    }
}
